/**
 * Cliente HTTP único da aplicação.
 *
 * Responsabilidades: montar a URL, anexar o Bearer token (ADR 0008), tentar um
 * refresh quando o access expira e normalizar erro da API no formato que o
 * `exception_handler` do backend devolve.
 */

import { API_BASE_URL } from '@/shared/api/config'
import { tokenStorage } from '@/shared/api/tokenStorage'

export interface ApiErrorBody {
  code: string
  message: string
  details?: Record<string, unknown>
}

export class ApiError extends Error {
  readonly status: number
  readonly code: string
  readonly details: Record<string, unknown>

  constructor(status: number, body: ApiErrorBody) {
    super(body.message)
    this.name = 'ApiError'
    this.status = status
    this.code = body.code
    this.details = body.details ?? {}
  }
}

type Options = Omit<RequestInit, 'body'> & { body?: unknown; auth?: boolean }

async function parseError(response: Response): Promise<ApiError> {
  let body: ApiErrorBody = { code: 'HttpError', message: response.statusText }
  try {
    const payload = await response.json()
    if (payload?.error) body = payload.error
    else if (payload?.detail) body = { code: 'HttpError', message: payload.detail }
  } catch {
    // resposta sem corpo JSON — mantém o fallback acima
  }
  return new ApiError(response.status, body)
}

async function refreshAccessToken(): Promise<boolean> {
  const refresh = tokenStorage.refresh
  if (!refresh) return false

  const response = await fetch(`${API_BASE_URL}/auth/token/refresh/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refresh }),
  })
  if (!response.ok) return false

  const { access } = (await response.json()) as { access: string }
  tokenStorage.saveAccess(access)
  return true
}

async function send(path: string, { body, auth = true, ...init }: Options): Promise<Response> {
  const headers = new Headers(init.headers)
  if (body !== undefined) headers.set('Content-Type', 'application/json')
  if (auth && tokenStorage.access) {
    headers.set('Authorization', `Bearer ${tokenStorage.access}`)
  }

  return fetch(`${API_BASE_URL}${path}`, {
    ...init,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
  })
}

async function request<T>(path: string, options: Options = {}): Promise<T> {
  let response = await send(path, options)

  if (response.status === 401 && options.auth !== false && (await refreshAccessToken())) {
    response = await send(path, options)
  }

  if (!response.ok) throw await parseError(response)
  if (response.status === 204) return undefined as T

  return (await response.json()) as T
}

export const api = {
  get: <T>(path: string, options?: Options) => request<T>(path, { ...options, method: 'GET' }),
  post: <T>(path: string, body?: unknown, options?: Options) =>
    request<T>(path, { ...options, method: 'POST', body }),
  patch: <T>(path: string, body?: unknown, options?: Options) =>
    request<T>(path, { ...options, method: 'PATCH', body }),
  delete: <T>(path: string, options?: Options) =>
    request<T>(path, { ...options, method: 'DELETE' }),
}

/** Texto de erro para a UI: a mensagem do backend quando é `ApiError`, senão um fallback. */
export function errorMessage(err: unknown, fallback = 'Algo deu errado. Tente de novo.'): string {
  return err instanceof ApiError ? err.message : fallback
}
